import { isExpectedAuthFailure } from '@shared/authErrors';
import { addSentryBreadcrumb, reportUserVisibleFailure } from '../../shared/sentry/sentryUser';
import {
  isUnexpectedFailureHandled,
  markUnexpectedFailureHandled,
} from '../../shared/sentry/presentUnexpectedFailure';

export type AuthOp =
  | 'signup'
  | 'signup.identity_check'
  | 'login'
  | 'reset'
  | 'resend'
  | 'sso'
  | 'oauth_callback'
  | 'update_password';

/**
 * Reporta a Sentry un fallo del flujo auth que el usuario ya vio (toast/error).
 * - Esperado (typo, ya registrado, rate limit, SSO cancelado) → breadcrumb
 *   `warning`, no issue.
 * - Inesperado (5xx, red, RPC roto, código desconocido, `validation_failed`
 *   residual) → issue con tag `user_visible`.
 * Nunca lanza: el reporting no rompe el flujo de auth.
 */
export function reportAuthFailure(
  op: AuthOp,
  error: unknown,
  opts?: { title?: string; expected?: boolean },
): void {
  try {
    if (isUnexpectedFailureHandled(error)) return;
    const expected = opts?.expected ?? isExpectedAuthFailure(error);
    if (expected) {
      const code = (error as { code?: unknown } | null)?.code;
      const status = (error as { status?: unknown } | null)?.status;
      addSentryBreadcrumb('auth', `${op}: expected auth failure`, 'warning', {
        code: typeof code === 'string' ? code : undefined,
        status: typeof status === 'number' ? status : undefined,
      });
      return;
    }
    const errorCode = (error as { code?: unknown } | null)?.code;
    reportUserVisibleFailure({
      module: 'auth',
      op,
      error,
      title: opts?.title,
      errorCode: typeof errorCode === 'string' && errorCode ? errorCode : undefined,
    });
  } catch {
    // Reporting must never replace the original failure path.
  }
}

/** Marca errores creados por el flujo (paths ya reportados/esperados) para que el catch no duplique el reporte. */
export const markAuthErrorHandled = markUnexpectedFailureHandled;
